"use client";

import { c as _c } from "react/compiler-runtime";
import { extractErrorCode } from "@local/error/src/extractErrorCode";
import { useParams } from "next/navigation";
import { useEffect, useTransition } from "react";
import { getT } from "@local/i18n/src/t";
import { getLanguageForMarket } from "@local/i18n/src/languages";
import { error as errorCatalog } from "@local/i18n/src/catalogs/error";
import { capturePostHogException } from "@local/tracking/src/scripts/posthog";
interface ErrorPageProps {
  error: Error & {
    digest?: string;
    code?: string;
  };
  /**
   * Next's `retry`, not `reset`. `reset()` only clears boundary state and
   * re-renders, so it cannot recover a Server Component error, which is what
   * nearly every error reaching this page is (an Offer API failure). `retry()`
   * re-fetches the segment. Stable since Next 16.3.
   */
  retry: () => void;
  heading?: string;
  /**
   * Catalog key (within the `error` catalog) resolved to the viewer's language
   * for the heading. Lets segment boundaries pass a localized heading without
   * duplicating the market/language wiring. `heading` takes precedence when set.
   */
  headingKey?: keyof (typeof errorCatalog)["en"];
}
export const ErrorPage = t0 => {
  const $ = _c(22);
  const {
    error,
    retry,
    heading,
    headingKey
  } = t0;
  const market = useParams().market as string | undefined;
  const t = getT(getLanguageForMarket(market), {
    error: errorCatalog
  });
  const [isRetrying, startRetry] = useTransition();
  const resolvedHeading = heading ?? t(`error.${headingKey ?? "errorHeading"}`);
  let t1;
  if ($[0] !== error) {
    t1 = extractErrorCode(error) ?? "STORE-X001";
    $[0] = error;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const errorCode = t1;
  let t2;
  if ($[2] !== error.digest) {
    t2 = error.digest?.slice(0, 8).toUpperCase();
    $[2] = error.digest;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const refCode = t2;
  let t3;
  let t4;
  if ($[4] !== error || $[5] !== errorCode) {
    t3 = () => {
      if (error.digest) {
        return;
      }
      capturePostHogException(error, {
        error_code: errorCode,
        boundary: "error-page"
      });
    };
    t4 = [error, errorCode];
    $[4] = error;
    $[5] = errorCode;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  if ($[8] !== errorCode) {
    t5 = <p>Error: {errorCode}</p>;
    $[8] = errorCode;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== refCode) {
    t6 = refCode && <p>Reference: {refCode}</p>;
    $[10] = refCode;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = <div className="mb-6 space-y-1 font-mono text-xs text-gray-400">{t5}{t6}</div>;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = "button";
  let t9;
  if ($[15] !== retry) {
    t9 = () => startRetry(() => retry());
    $[15] = retry;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = t("error.errorRetryAria");
  const t11 = "min-h-11 rounded-full bg-gray-900 px-6 py-2 text-white transition-colors hover:bg-gray-700 focus-visible:ring-2 focus-visible:ring-gray-700 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-40 sm:min-h-[50px]";
  const t12 = t("error.errorRetry");
  let t13;
  if ($[17] !== isRetrying || $[18] !== t10 || $[19] !== t12 || $[20] !== t9) {
    t13 = <button type={t8} onClick={t9} disabled={isRetrying} aria-label={t10} className={t11}>{t12}</button>;
    $[17] = isRetrying;
    $[18] = t10;
    $[19] = t12;
    $[20] = t9;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  return <div role="alert" id="error-page" className="flex min-h-[50vh] flex-col items-center justify-center px-4 text-center"><h5 className="text-h5 leading-heading mb-4 font-semibold">{resolvedHeading}</h5><p className="mb-2 text-gray-600">{t("error.errorBody")}</p>{t7}{t13}</div>;
};